import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";

import { attributionColumns, attributionFields } from "@/lib/attribution-schema";

const bookingSchema = z.object({
  name: z.string().trim().min(2, "Please tell us your name").max(120),
  email: z.string().trim().email("Please enter a valid email address").max(200),
  phone: z.string().trim().min(6, "Please give us a phone number").max(40),
  matterType: z.enum([
    "conveyancing-purchase",
    "conveyancing-sale",
    "wills-estates",
    "elder-law",
    "business-commercial",
    "other",
  ]),
  meetingType: z.enum(["in-person", "phone", "video"]),
  preferredDate: z
    .string()
    .trim()
    .regex(/^\d{4}-\d{2}-\d{2}$/, "Please choose a date")
    .optional()
    .or(z.literal("")),
  preferredTime: z.enum(["morning", "midday", "afternoon", "after-hours"]),
  alternateNotes: z.string().trim().max(400).optional().or(z.literal("")),
  message: z.string().trim().max(4000).optional().or(z.literal("")),
  sourcePage: z.string().trim().max(200).optional().or(z.literal("")),
  ...attributionFields,
});

export type BookingInput = z.infer<typeof bookingSchema>;

export const submitBookingRequest = createServerFn({ method: "POST" })
  .inputValidator((data: unknown) => bookingSchema.parse(data))
  .handler(async ({ data }) => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");

    const { error } = await supabaseAdmin.from("booking_requests").insert({
      name: data.name,
      email: data.email,
      phone: data.phone,
      matter_type: data.matterType,
      meeting_type: data.meetingType,
      preferred_date: data.preferredDate || null,
      preferred_time: data.preferredTime,
      alternate_notes: data.alternateNotes || null,
      message: data.message || null,
      source_page: data.sourcePage || null,
      ...attributionColumns(data),
    });

    if (error) {
      console.error("Failed to store booking request", error);
      throw new Error("We could not book that in. Please call us instead.");
    }

    const { sendLeadToCrm } = await import("@/lib/crm.server");
    await sendLeadToCrm({
      formType: "booking-request",
      name: data.name,
      email: data.email,
      phone: data.phone,
      matterType: data.matterType,
      sourcePage: data.sourcePage,
      message: data.message,
      details: {
        meeting_type: data.meetingType,
        preferred_date: data.preferredDate,
        preferred_time: data.preferredTime,
        alternate_notes: data.alternateNotes,
      },
    });

    return { ok: true } as const;
  });
